<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <form @submit.prevent="saveTicket(newUser, applyPhone)">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Create ticket') }}</h1>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div ref="entity" class="mt-6 shadow sm:rounded-lg">
                    <loading :status="loading.entity"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Advanced search') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Advanced search of Organizations, Centers and Users') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="subject">{{ $t('Support code') }}</label>
                                    <div class="inline-flex items-center gap-3 w-full mt-1 relative">
                                        <input
                                            id="org_code"
                                            v-model="orgCode"
                                            placeholder="00000"
                                            class="form-input block w-24 transition duration-150 ease-in-out text-center shadow-sm sm:text-sm sm:leading-5 focus:z-10"
                                            type="text"
                                            maxlength="5"
                                            @keyup="inputSupport($event)"
                                        >
                                        <div class="text-lg font-bold text-gray-600">-</div>
                                        <input
                                            id="user_code"
                                            v-model="userCode"
                                            placeholder="AA"
                                            class="form-input block w-16 transition duration-150 ease-in-out text-center shadow-sm sm:text-sm sm:leading-5 focus:z-10"
                                            type="text"
                                            maxlength="2"
                                            @keyup="inputSupport($event)"
                                        >
                                        <button
                                            class="btn btn-blue shadow-sm"
                                            type="button"
                                            @click="searchByCode()"
                                        >
                                            {{ $t('Search') }}
                                        </button>
                                    </div>
                                </div>
                                <hr class="col-span-3">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="server">{{ $t('Server') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="server"
                                            v-model="server"
                                            :options="serverShow"
                                            option-label="name"
                                            :option-max=200
                                            @input="selectServer()"
                                            status
                                            searchable
                                        >
                                            <template v-slot:selectedOption="props">
                                                <template v-if="props && !props.anySelected">
                                                    <span class="block truncate">{{ $t('Select an option') }}</span>
                                                </template>
                                                <template v-else>
                                                    <div class="flex-shrink-0 inline-block">
                                                        <svg-vue class="w-5 h-auto object-scale-down text-gray-500" icon="font-awesome.server-solid"></svg-vue>
                                                    </div>
                                                    <span class="block truncate">{{ props.option.name }}</span>
                                                    <div class="hidden xl:flex xl:items-center xl:gap-1 truncate font-normal text-gray-500">
                                                        <svg-vue class="h-4 w-4 text-gray-500" icon="font-awesome.link-solid"></svg-vue>
                                                        <span>{{ props.option.url ? props.option.url : "-" }}</span>
                                                    </div>
                                                </template>
                                            </template>
                                            <template v-slot:selectOption="props">
                                                <div class="flex items-center space-x-3">
                                                    <div class="flex-shrink-0 inline-block">
                                                        <svg-vue class="w-5 h-auto object-scale-down text-gray-500" icon="font-awesome.server-solid"></svg-vue>
                                                    </div>
                                                    <div class="font-normal block truncate">
                                                        {{ props.option.name }}
                                                    </div>
                                                    <div class="hidden xl:flex xl:items-center xl:gap-2 truncate font-normal text-gray-500">
                                                        <svg-vue class="h-3 w-3 text-gray-500" icon="font-awesome.link-solid"></svg-vue>
                                                        <span>{{ props.option.url ? props.option.url : "-" }}</span>
                                                    </div>
                                                </div>
                                            </template>
                                        </input-select>
                                    </div>
                                    <div v-show="serverSearch.length > 0" class="grid grid-cols-1 xl:grid-cols-2 gap-2 mt-3 max-h-52 overflow-y-auto">
                                        <div v-for="(item, index) in serverSearch"
                                            class="flex items-center col-span-1 p-2 px-3 space-x-3 bg-indigo-100 hover:bg-indigo-200 cursor-pointer rounded-sm"
                                            @click="server = item.id; selectServer()"
                                        >
                                            <div class="flex-shrink-0 inline-block">
                                                <svg-vue class="h-4 w-4 text-gray-500" icon="font-awesome.server-solid"></svg-vue>
                                            </div>
                                            <div class="text-sm font-normal block truncate">
                                                {{ item.name }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="org">{{ $t('Organization') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="org"
                                            v-model="org"
                                            :options="orgShow"
                                            option-label="name"
                                            :option-max=200
                                            @input="selectOrg()"
                                            status
                                            searchable
                                        >
                                            <template v-slot:selectedOption="props">
                                                <template v-if="props && !props.anySelected">
                                                    <span class="block truncate">{{ $t('Select an option') }}</span>
                                                </template>
                                                <template v-else>
                                                    <div class="flex-shrink-0 inline-block">
                                                        <img :src="props.option.avatar" alt="Icon" class="w-5 h-auto rounded-full object-scale-down">
                                                    </div>
                                                    <span class="block truncate">{{ props.option.name }}</span>
                                                </template>
                                            </template>
                                            <template v-slot:selectOption="props">
                                                <div class="flex items-center space-x-3">
                                                    <div class="flex-shrink-0 inline-block">
                                                        <img :src="props.option.avatar" alt="Icon" class="w-5 h-auto rounded-full object-scale-down">
                                                    </div>
                                                    <div class="font-normal block truncate">
                                                        {{ props.option.name }}
                                                    </div>
                                                </div>
                                            </template>
                                        </input-select>
                                    </div>
                                    <div v-if="orgObservations" class="grid grid-cols-1 xl:grid-cols-2 gap-2 mt-3 text-sm text-gray-600">
                                        {{ orgObservations }}
                                    </div>
                                    <div v-show="orgSearch.length > 0" class="grid grid-cols-1 xl:grid-cols-2 gap-2 mt-3 max-h-52 overflow-y-auto">
                                        <div v-for="(item, index) in orgSearch"
                                            class="flex items-center col-span-1 p-2 px-3 space-x-3 bg-indigo-100 hover:bg-indigo-200 cursor-pointer rounded-sm"
                                            @click="org = item.id; selectOrg()"
                                        >
                                            <div class="flex-shrink-0 inline-block">
                                                <img :src="item.avatar" alt="Icon" class="w-5 h-auto rounded-full object-scale-down">
                                            </div>
                                            <div class="text-sm font-normal block truncate">
                                                {{ item.name }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="center">{{ $t('Center') }}<strong v-show="newUser" class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="center"
                                            v-model="center"
                                            :options="centerShow"
                                            option-label="name"
                                            :option-max=200
                                            @input="selectCenter()"
                                            :required="newUser"
                                            status
                                            searchable
                                        >
                                            <template v-slot:selectedOption="props">
                                                <template v-if="props && !props.anySelected">
                                                    <span class="block truncate">{{ $t('Select an option') }}</span>
                                                </template>
                                                <template v-else>
                                                    <div class="flex-shrink-0 inline-block">
                                                        <img :src="props.option.avatar" alt="Icon" class="w-5 h-auto rounded-full object-scale-down">
                                                    </div>
                                                    <span class="block truncate">{{ props.option.name }}</span>
                                                </template>
                                            </template>
                                            <template v-slot:selectOption="props">
                                                <div class="flex items-center space-x-3">
                                                    <div class="flex-shrink-0 inline-block">
                                                        <img :src="props.option.avatar" alt="Icon" class="w-5 h-auto rounded-full object-scale-down">
                                                    </div>
                                                    <div class="font-normal block truncate">
                                                        {{ props.option.name }}
                                                    </div>
                                                    <div class="flex items-center space-x-1.5 font-normal text-gray-500">
                                                        <svg-vue class="flex-shrink-0 h-4 w-4 text-gray-500" icon="font-awesome.clipboard-regular"></svg-vue>
                                                        <span class="truncate">{{ $t(props.option.status_gdr) }}</span>
                                                        <span v-for="formation_id in props.option.formation_ids" class="truncate text-green-400 text-xs">{{ formationList.filter(f => f.id == formation_id)[0]?.name }}</span>
                                                    </div>
                                                </div>
                                            </template>
                                        </input-select>
                                    </div>
                                    <div v-if="centerObservations" class="grid grid-cols-1 xl:grid-cols-2 gap-2 mt-3 text-sm text-gray-600">
                                        {{ centerObservations }}
                                    </div>
                                    <div v-show="centerSearch.length > 0" class="grid grid-cols-1 xl:grid-cols-2 gap-2 mt-3 max-h-52 overflow-y-auto">
                                        <div v-for="(item, index) in centerSearch"
                                            class="flex items-center col-span-1 p-2 px-3 space-x-3 bg-indigo-100 hover:bg-indigo-200 cursor-pointer rounded-sm"
                                            @click="center = item.id; selectCenter()"
                                        >
                                            <div class="flex-shrink-0 inline-block">
                                                <img :src="item.avatar" alt="Icon" class="w-5 h-auto rounded-full object-scale-down">
                                            </div>
                                            <div class="text-sm font-normal block truncate">
                                                {{ item.name }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="customer">{{ $t('Customer') }}<strong class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="customer"
                                            v-model="user"
                                            :options="userShow"
                                            option-label="name"
                                            :option-max=200
                                            @input="selectUser()"
                                            users
                                            status
                                            searchable
                                            :min-search=3
                                            required
                                        >
                                            <template v-slot:selectedOption="props">
                                                <template v-if="props && !props.anySelected">
                                                    <span class="block truncate">{{ $t('Select an option') }}</span>
                                                </template>
                                                <template v-else>
                                                    <div class="flex-shrink-0 inline-block">
                                                        <img :src="props.option.avatar" alt="Icon" class="w-5 h-auto rounded-full object-scale-down">
                                                    </div>
                                                    <span class="block truncate">{{ props.option.name }}</span>
                                                </template>
                                            </template>
                                            <template v-slot:selectOption="props">
                                                <div class="flex items-center space-x-3">
                                                    <div class="flex-shrink-0 inline-block">
                                                        <img :src="props.option.avatar" alt="Icon" class="w-5 h-auto rounded-full object-scale-down">
                                                    </div>
                                                    <div :class="props.option.id === ticket.user_id ? 'font-semibold' : 'font-normal'" class="font-normal block truncate">
                                                        {{ props.option.name }}
                                                    </div>
                                                </div>
                                            </template>
                                        </input-select>
                                    </div>
                                    <div v-if="userObservations" class="grid grid-cols-1 xl:grid-cols-2 gap-2 mt-3 text-sm text-gray-600">
                                        {{ userObservations }}
                                    </div>
                                    <div v-show="userSearch.length > 0" class="grid grid-cols-1 xl:grid-cols-2 gap-2 mt-3 max-h-52 overflow-y-auto">
                                        <div v-for="(item, index) in userSearch"
                                            class="flex items-center col-span-1 p-2 px-3 space-x-3 bg-indigo-100 hover:bg-indigo-200 cursor-pointer rounded-sm"
                                            @click="user = item.id; selectUser()"
                                        >
                                            <div class="flex-shrink-0 inline-block">
                                                <img :src="item.avatar" alt="Icon" class="w-5 h-auto rounded-full object-scale-down">
                                            </div>
                                            <div class="text-sm font-normal block truncate">
                                                {{ item.name }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="userView.id" class="col-span-3">
                                    <div class="flex items-center gap-6">
                                        <div class="flex-shrink-0">
                                            <img :alt="$t('Avatar')" :src="userView.avatar" class="block m-auto h-24 w-24 object-scale-down">
                                            <support-code
                                                :orgCode="userView.centers[0]?.organization?.support_code"
                                                :userSupport="userView.support == 1 ? true : false"
                                                :userCode="userView.support_code"
                                                :isLocal="userView.id_ext == null"
                                                :isAdmin="userView.role_id == 1"
                                                wrapper="w-28 mt-3"
                                            >
                                            </support-code>
                                        </div>
                                        <div class="min-w-0 flex-1">
                                            <div class="flex items-center text-base leading-5 font-medium text-blue-600 truncate">
                                                <span>{{ userView.name }}</span>
                                                <span class="ml-2.5 truncate font-normal text-blue-500">
                                                    <span v-for="(phone, index) in userView.phones">
                                                        <span v-if="index == 0" class="mr-1">/</span>
                                                        <span>{{ phone.country_code == "34" ? phone.number : "+" + phone.country_code + phone.number }}</span>
                                                        <span v-if="index + 1 < userView.phones.length" class="mx-1">/</span>
                                                    </span>
                                                </span>
                                            </div>
                                            <div class="mt-2 flex items-center text-sm leading-5 text-gray-600">
                                                <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-500" icon="font-awesome.envelope-solid"></svg-vue>
                                                <span class="truncate">{{ userView.email ? userView.email : "-" }}</span>
                                            </div>
                                            <div v-if="userView.centers.length > 0" class="flex items-center mt-2 text-sm leading-5 text-gray-500 truncate">
                                                <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-500" icon="font-awesome.building-regular"></svg-vue>
                                                <span class="truncate">{{ userView.centers[0].organization ? userView.centers[0].organization.name : '-' }}</span>
                                            </div>
                                            <div v-else class="flex items-center mt-2 text-sm leading-5 text-gray-500 truncate">
                                                <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-500" icon="font-awesome.building-regular"></svg-vue>
                                                <span class="truncate">-</span>
                                            </div>
                                            <div v-for="(center, index) in userView.centers" class="flex items-center mt-2 text-sm leading-5 text-gray-500 truncate">
                                                <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-500" icon="font-awesome.hospital-regular"></svg-vue>
                                                <span class="truncate">{{ center.name }}</span>
                                            </div>
                                            <div v-if="userView.centers.length == 0" class="flex items-center mt-2 text-sm leading-5 text-gray-500 truncate">
                                                <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-500" icon="font-awesome.hospital-regular"></svg-vue>
                                                <span class="truncate">-</span>
                                            </div>
                                            <div v-if="userView.profile" class="mt-2 flex items-center text-sm leading-5 text-gray-500">
                                                <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-500" icon="font-awesome.id-badge-regular"></svg-vue>
                                                <span class="truncate">{{ userView.profile.name }}</span>
                                            </div>
                                            <div v-else class="mt-2 flex items-center text-sm leading-5 text-gray-500">
                                                <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-500" icon="font-awesome.id-badge-regular"></svg-vue>
                                                <span class="truncate">-</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <hr class="col-span-3">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="phone">{{ $t('Phone') }}</label>
                                    <div class="inline-flex w-full mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="phone_country"
                                            v-model="phone.country"
                                            placeholder="ES"
                                            class="form-input block w-1/5 sm:w-16 border-r-0 rounded-r-none transition duration-150 ease-in-out text-center sm:text-sm sm:leading-5 focus:z-10"
                                            type="text"
                                        >
                                        <input
                                            id="phone_country_code"
                                            v-model="phone.countryCallingCode"
                                            placeholder="34"
                                            class="form-input block w-1/5 sm:w-16 rounded-none transition duration-150 ease-in-out text-center sm:text-sm sm:leading-5 focus:z-10"
                                            type="text"
                                        >
                                        <input
                                            id="phone_number"
                                            v-model="phone.nationalNumber"
                                            :placeholder="$t('Phone')"
                                            class="form-input block w-3/5 sm:w-full border-l-0 rounded-l-none transition duration-150 ease-in-out sm:text-sm sm:leading-5 focus:z-10"
                                            type="text"
                                        >
                                    </div>
                                </div>
                                <div v-show="ticket.user_id != null && ticket.user_id > 0" class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="applyPhone">{{ $t('Set phone to user') }}</label>
                                    <input-switch
                                        id="applyPhone"
                                        v-model="applyPhone"
                                        :disabled-label="$t('The phone number will not be set')"
                                        :enabled-label="$t('The phone number will be set to the user')"
                                    ></input-switch>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                        <div class="inline-flex">
                            <button
                                class="btn btn-secondary shadow-sm rounded-md mr-4"
                                type="button"
                                @click="clearEntities()"
                            >
                                <svg-vue class="w-4 h-4" icon="font-awesome.sync-regular"></svg-vue>
                            </button>
                            <button
                                class="btn btn-blue shadow-sm rounded-md"
                                type="button"
                                @click="lastAPIphone()"
                            >
                                {{ $t('Get call') }}
                            </button>
                        </div>
                    </div>
                </div>
                <transition name="fade">
                    <div ref="newUser" v-show="newUser" class="mt-6 shadow sm:rounded-lg">
                        <loading :status="loading.newUser"/>
                        <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                            <div class="md:col-span-1">
                                <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Create user') }}</h3>
                                <p class="mt-1 text-sm leading-5 text-gray-500">
                                    {{ $t('Create user') }}.
                                </p>
                            </div>
                            <div class="mt-5 md:mt-0 md:col-span-2">
                                <div class="grid grid-cols-3 gap-6">
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="name">{{ $t('Name') }}<strong v-show="newUser" class="text-red-600"> *</strong></label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="name"
                                                v-model="userNew.name"
                                                :placeholder="$t('Name')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                :required="newUser"
                                            >
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="email">{{ $t('Email') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="email"
                                                v-model="userNew.email"
                                                :placeholder="$t('Email')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                type="email"
                                            >
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="profile">{{ $t('Profile') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input-select
                                                id="profile"
                                                v-model="userNew.profile_id"
                                                :options="profileList"
                                                option-label="name"
                                                searchable
                                            >
                                                <template v-slot:selectedOption="props">
                                                    <template v-if="props && !props.anySelected">
                                                        <span class="block truncate">{{ $t('Select an option') }}</span>
                                                    </template>
                                                    <template v-else>
                                                        <span class="block truncate">{{ props.option.name }}</span>
                                                    </template>
                                                </template>
                                                <template v-slot:selectOption="props">
                                                    <div class="flex items-center space-x-3">
                                                        <div :class="props.option.id === userNew.profile_id ? 'font-semibold' : 'font-normal'" class="font-normal block truncate">
                                                            {{ props.option.name }}
                                                        </div>
                                                    </div>
                                                </template>
                                            </input-select>
                                        </div>
                                    </div>
                                    <hr v-show="newProfile" class="col-span-3">
                                    <div v-show="newProfile" class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="profile_name">{{ $t('Name') }}<strong v-show="(newUser && newProfile)" class="text-red-600"> *</strong></label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="profile_name"
                                                v-model="profile.name"
                                                :placeholder="$t('Profile')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                :required="newUser && newProfile"
                                            >
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                            <div class="inline-flex">
                                <button
                                    class="btn btn-secondary shadow-sm rounded-md"
                                    type="button"
                                    @click="clearNewUser()"
                                >
                                    <svg-vue class="w-4 h-4" icon="font-awesome.sync-regular"></svg-vue>
                                </button>
                            </div>
                        </div>
                    </div>
                </transition>
                <div ref="form" class="mt-6 shadow sm:rounded-lg">
                    <loading :status="loading.form"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Ticket details') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Ticket details and classification') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="subject">{{ $t('Subject') }}<strong class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="subject"
                                            v-model="ticket.subject"
                                            :placeholder="$t('Subject')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="department">{{ $t('Department') }}<strong class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="department"
                                            v-model="ticket.department_id"
                                            :options="departmentList"
                                            option-label="name"
                                            required
                                        />
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="status">{{ $t('Status') }}<strong class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="status"
                                            v-model="ticket.status_id"
                                            :options="statusList"
                                            option-label="name"
                                            required
                                        />
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="priority">{{ $t('Priority') }}<strong class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="priority"
                                            v-model="ticket.priority_id"
                                            :options="priorityList"
                                            option-label="name"
                                            required
                                        />
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="ticket_body">{{ $t('Ticket body') }}<strong class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-wysiwyg
                                            id="ticket_body"
                                            v-model="ticket.body"
                                            :cannedReplyList="cannedReplyList"
                                            :plugins="{images: true, cannedReply: true, attachment: true, shortCode: true}"
                                            @selectUploadFile="selectUploadFile"
                                        >
                                            <template v-slot:top>
                                                <div :class="{'bg-gray-200': uploadingFileProgress > 0}" class="h-1 w-full">
                                                    <div :style="{width: uploadingFileProgress + '%'}" class="bg-blue-500 py-0.5"></div>
                                                </div>
                                            </template>
                                        </input-wysiwyg>
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="sendMail">{{ $t('Send mail to customer') }}</label>
                                    <input-switch
                                        id="sendMail"
                                        v-model="ticket.sendMail"
                                        :disabled-label="$t('No mail will be sent')"
                                        :enabled-label="$t('An email will be sent to the customer')"
                                    ></input-switch>
                                </div>
                                <div v-if="ticket.attachments.length > 0" class="col-span-3">
                                    <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2">
                                        <template v-for="(attachment, index) in ticket.attachments">
                                            <attachment :details="attachment" v-on:remove="removeAttachment(index)"/>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                        <div class="inline-flex">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded-md mr-4"
                                to="/dashboard/tickets"
                            >
                                {{ $t('Cancel') }}
                            </router-link>
                            <button
                                class="btn btn-secondary shadow-sm rounded-md mr-4"
                                type="button"
                                @click="clearDetails()"
                            >
                                <svg-vue class="w-4 h-4" icon="font-awesome.sync-regular"></svg-vue>
                            </button>
                            <button
                                class="btn btn-green shadow-sm rounded-md"
                                type="submit"
                            >
                                {{ $t('Create ticket') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </form>
        <input ref="fileInput" hidden type="file" @change="uploadFile($event)">
    </main>
</template>

<script>
import axios from 'axios';
import { get } from 'http';
import parsePhoneNumber from 'libphonenumber-js'

export default {
    name: "new",
    metaInfo() {
        return {
            title: this.$i18n.t('Create ticket')
        }
    },
    data() {
        return {
            // Async loading
            loading: {
                entity: true,
                newUser: true,
                form: true,
                file: false,
            },
            uploadingFileProgress: 0,
            // Ticket data
            ticket: {
                user_id: null,
                subject: null,
                phones: [],
                department_id: null,
                status_id: 1,
                priority_id: 1,
                body: '',
                note: false,
                attachments: [],
                sendMail: false,
            },
            // Selected IDs
            server: null,
            org: null,
            center: null,
            user: null,
            // New user
            userNew: {
                id_ext: null,
                name: null,
                email: null,
                avatar: null,
                support: false,
                support_code: null,
                phones: [],
                phone_ids: [],
                role_id: 3,
                profile_id: null,
                center_ids: null,
                status: true,
                password: null,
            },
            userView: {
                id: null,
                id_ext: null,
                name: null,
                email: null,
                avatar: null,
                support_code: null,
                phones: [],
                phone_ids: [],
                role: null,
                role_id: 3,
                profile: null,
                profile_id: null,
                centers: [],
                center_ids: [],
                status: true,
                created_at: '',
                updated_at: ''
            },
            // New phone
            phone: {
                id: null,
                country: 'ES',
                countryCallingCode: 34,
                nationalNumber: null,
                number: null,
            },
            // New profile
            profile: {
                name: null,
            },
            // Observations
            orgObservations: null,
            centerObservations: null,
            userObservations: null,
            // Support code
            orgCode: null,
            userCode: null,
            // Options
            applyPhone: false,
            newUser: false,
            newUserId: -1,
            newProfile: false,
            newProfileId: -1,
            ticketFailed: false,
            // Resource lists
            departmentList: [],
            serverList: [],
            orgList: [],
            centerList: [],
            userList: [],
            profileList: [],
            statusList: [],
            priorityList: [],
            cannedReplyList: [],
            formationList: [],
            // Dirty lists
            serverShow: [],
            orgShow: [],
            centerShow: [],
            userShow: [],
            // Advanced search
            serverSearch: [],
            orgSearch: [],
            centerSearch: [],
            userSearch: [],
            // Async loading
            asyncLoad: {
                loading: 0,
                entity: 0,
                newUser: 0,
                form: 0,
                file: 0,
                details: 0,
                password: 0,
                reply: 0,
            },
        }
    },
    computed: {
        profileId() {
            return this.userNew.profile_id;
        },
        orgCodeFormatted: {
            get() {
                return this.orgCode;
            },
            set(newValue) {
                this.orgCode = newValue.replace(/\D/g,'');
            }
        },
        userCodeFormatted: {
            get() {
                return this.userCode;
            },
            set(newValue) {
                this.userCode = newValue.toUpperCase();
            }
        }
    },
    watch: {
        user(newValue, oldValue) {
            this.ticket.user_id = newValue;
            if (newValue === null || newValue == this.newUserId) this.userView.id = null;
        },
        profileId(newValue, oldValue) {
            this.newProfile = (newValue == this.newProfileId);
        },
    },
    mounted() {
        this.getData();
    },
    methods: {
        async getData() {
            const self = this;
            let requests = [];
            requests.push(self.getUsers());
            requests.push(self.getCenters());
            requests.push(self.getOrgs());
            requests.push(self.getServers());
            requests.push(self.getProfiles());
            requests.push(self.getFilters());
            requests.push(self.getCannedReplies());
            requests.push(self.getFormations());
            await Promise.all(requests);
            return requests;
        },
        getServers() {
            const self = this;
            self.asyncLoading("entity");
            return axios.get('api/dashboard/admin/servers/list', {
                params: {
                    status: true,
                    light: true
                }
            }).then(async (response) => {
                self.setServerList(response.data.servers, true);
                self.asyncLoading("entity", false);
            }).catch(function () {
                self.asyncLoading("entity", false);
            });
        },
        getOrgs() {
            const self = this;
            self.asyncLoading("entity");
            return axios.get('api/dashboard/admin/orgs/list', {
                params: {
                    include_all_except: ['centers'],
                    status: true,
                    light: true
                }
            }).then((response) => {
                self.setOrgList(response.data.orgs, true);
                self.asyncLoading("entity", false);
            }).catch(function () {
                self.asyncLoading("entity", false);
            });
        },
        getCenters() {
            const self = this;
            self.asyncLoading("entity");
            return axios.get('api/dashboard/admin/centers/list', {
                params: {
                    include_all_except: ['users'],
                    status: true,
                    light: true
                }
            }).then((response) => {
                self.setCenterList(response.data.centers, true);
                self.asyncLoading("entity", false);
            }).catch(function () {
                self.asyncLoading("entity", false);
            });
        },
        getUsers(ids = []) {
            const self = this;
            self.asyncLoading("entity");
            return axios.get('api/dashboard/admin/users/list', {
                params: {
                    include: ['userRole', 'phones', 'centers'],
                    ids: ids,
                    status: true,
                    light: true
                }
            }).then((response) => {
                self.setUserList(response.data.users, true);
                self.asyncLoading("entity", false);
            }).catch(function () {
                self.asyncLoading("entity", false);
            });
        },
        getUserView(id) {
            const self = this;
            self.asyncLoading("entity");
            return axios.get('api/dashboard/admin/users/list', {
                params: {
                    include_all_except: ['userTickets', 'agentTickets'],
                    include: ['centers.org'],
                    ids: [id],
                    light: false
                }
            }).then((response) => {
                self.userView = response.data.users[0];
                self.asyncLoading("entity", false);
            }).catch(function () {
                self.asyncLoading("entity", false);
            });
        },
        getProfiles() {
            const self = this;
            self.asyncLoading("newUser");
            return axios.get('api/dashboard/admin/profiles').then(function (response) {
                let newProfileOption = {
                    id: self.newProfileId,
                    name: "+ " + self.$i18n.t('Create profile'),
                };
                self.profileList = [...response.data];
                self.profileList.unshift(newProfileOption);
                self.asyncLoading("newUser", false);
            }).catch(function () {
                self.asyncLoading("newUser", false);
            });
        },
        getFilters() {
            const self = this;
            self.asyncLoading("form");
            return axios.get('api/dashboard/tickets/filters').then(function (response) {
                self.departmentList = response.data.departments;
                self.statusList = response.data.statuses;
                self.priorityList = response.data.priorities;
                self.asyncLoading("form", false);
            }).catch(function () {
                self.asyncLoading("form", false);
            })
        },
        getCannedReplies() {
            const self = this;
            return axios.get('api/dashboard/tickets/canned-replies').then(function (response) {
                self.cannedReplyList = response.data;
            })
        },
        getFormations() {
            const self = this;
            self.asyncLoading("entity");
            axios.get('api/dashboard/admin/formations').then(function (response) {
                self.formationList = response.data;
                self.asyncLoading("entity", false);
            }).catch(function () {
                self.asyncLoading("entity", false);
            });
        },
        setServerList(list = [], setRootList = false) {
            const self = this;
            self.server = null;
            if (setRootList) self.serverList = [...list];
            self.serverShow = [...list];
        },
        setOrgList(list = [], setRootList = false) {
            const self = this;
            self.org = null;
            if (setRootList) self.orgList = [...list];
            self.orgShow = [...list];
        },
        setCenterList(list = [], setRootList = false) {
            const self = this;
            self.center = null;
            if (setRootList) self.centerList = [...list];
            self.centerShow = [...list];
        },
        setUserList(list = [], setRootList = false) {
            const self = this;
            if (!self.newUser) self.user = null;
            let newUserOption = {
                id: self.newUserId,
                id_ext: null,
                name: self.$i18n.t('Create user'),
                email: null,
                phones: [],
                avatar: "https://" + window.location.hostname + "/images/default/add.png",
                role_id: null,
                center_ids: [],
                status: true,
                created_at: null,
                updated_at: null
            };
            if (setRootList) self.userList = [...list];
            self.userShow = [...list];
            self.userShow.unshift(newUserOption);
        },
        setServerSearch(ids = []) {
            const self = this;
            self.serverSearch = [];
            if (self.serverList.length > 0 && ids.length > 0) {
                for (let i = 0; i < self.serverList.length; i++) {
                    if (ids.some(id => id == self.serverList[i].id)) {
                        self.serverSearch.push(self.serverList[i]);
                    }
                }
            }
        },
        setOrgSearch(ids = []) {
            const self = this;
            self.orgSearch = [];
            if (self.orgList.length > 0 && ids.length > 0) {
                for (let i = 0; i < self.orgList.length; i++) {
                    if (ids.some(id => id == self.orgList[i].id)) {
                        self.orgSearch.push(self.orgList[i]);
                    }
                }
            }
        },
        setCenterSearch(ids = []) {
            const self = this;
            self.centerSearch = [];
            if (self.centerList.length > 0 && ids.length > 0) {
                for (let i = 0; i < self.centerList.length; i++) {
                    if (ids.some(id => id == self.centerList[i].id)) {
                        self.centerSearch.push(self.centerList[i]);
                    }
                }
            }
        },
        setUserSearch(ids = []) {
            const self = this;
            self.userSearch = [];
            if (self.userList.length > 0 && ids.length > 0) {
                for (let i = 0; i < self.userList.length; i++) {
                    if (ids.some(id => id == self.userList[i].id)) {
                        self.userSearch.push(self.userList[i]);
                    }
                }
            }
        },
        selectServer(filterBelow = true) {
            const self = this;
            if (!self.serverShow.some(s => s.id == self.server)) {
                let serverId = self.server;
                self.resetShowLists();
                self.server = serverId;
            }
            if (filterBelow) {
                // Temp lists
                let orgTmp = [];
                let centerTmp = [];
                let userTmp = [];
                // Filter orgs
                for (let i = 0; i < self.orgList.length; i++) {
                    if (self.server == self.orgList[i].server_id) {
                        orgTmp.push(self.orgList[i]);
                    }
                }
                self.setOrgList(orgTmp);
                // Filter centers
                for (let i = 0; i < self.orgShow.length; i++) {
                    for (let j = 0; j < self.centerList.length; j++) {
                        if (self.orgShow[i].id == self.centerList[j].org_id && !centerTmp.some(c => c.id == self.centerList[j].id)) {
                            centerTmp.push(self.centerList[j]);
                        }
                    }
                }
                self.setCenterList(centerTmp);
                // Filter users
                for (let i = 0; i < self.centerShow.length; i++) {
                    for (let j = 0; j < self.userList.length; j++) {
                        if (self.userList[j].center_ids.some(id => id == self.centerShow[i].id) && !userTmp.some(u => u.id == self.userList[j].id)) {
                            userTmp.push(self.userList[j]);
                        }
                    }
                }
                self.setUserList(userTmp);
            }
        },
        selectOrg(filterBelow = true) {
            const self = this;
            if (!self.orgShow.some(o => o.id == self.org)) {
                let orgId = self.org;
                self.resetShowLists();
                self.org = orgId;
            }
            // Select the server
            if (self.org && self.serverShow.length > 0) {
                for (let i = 0; i < self.orgShow.length; i++) {
                    if (self.org == self.orgShow[i].id) {
                        self.orgObservations = self.orgShow[i].observations;
                        self.server = self.orgShow[i].server_id;
                        break;
                    }
                }
            }
            self.centerObservations = null;
            self.userObservations = null;
            if (filterBelow) {
                // Temp lists
                let centerTmp = [];
                let userTmp = [];
                // Filter centers
                for (let i = 0; i < self.centerList.length; i++) {
                    if (self.org == self.centerList[i].org_id) {
                        centerTmp.push(self.centerList[i]);
                    }
                }
                self.setCenterList(centerTmp);
                // Filter users
                for (let i = 0; i < self.centerShow.length; i++) {
                    for (let j = 0; j < self.userList.length; j++) {
                        if (self.userList[j].center_ids.some(id => id == self.centerShow[i].id) && !userTmp.some(u => u.id == self.userList[j].id)) {
                            userTmp.push(self.userList[j]);
                        }
                    }
                }
                self.setUserList(userTmp);
            }
        },
        selectCenter(filterBelow = true) {
            const self = this;
            if (!self.centerShow.some(c => c.id == self.center)) {
                let centerId = self.center;
                self.resetShowLists();
                self.center = centerId;
            }
            // Select the org
            if (self.center && self.centerShow.length > 0) {
                for (let i = 0; i < self.centerShow.length; i++) {
                    if (self.center == self.centerShow[i].id) {
                        self.centerObservations = self.centerShow[i].observations;
                        self.org = self.centerShow[i].org_id;
                        break;
                    }
                }
                // Select the server
                if (self.org && self.serverShow.length > 0) {
                    for (let i = 0; i < self.orgShow.length; i++) {
                        if (self.org == self.orgShow[i].id) {
                            self.orgObservations = self.orgShow[i].observations;
                            self.server = self.orgShow[i].server_id;
                            break;
                        }
                    }
                }
            }
            self.userObservations = null;
            if (filterBelow) {
                // Temp lists
                let userTmp = [];
                // Filter users
                for (let i = 0; i < self.userList.length; i++) {
                    if (self.userList[i].center_ids.some(id => id == self.center)) {
                        userTmp.push(self.userList[i]);
                    }
                }
                self.setUserList(userTmp);
            }
        },
        selectUser() {
            const self = this;
            if (!self.userShow.some(u => u.id == self.user)) {
                let userId = self.user;
                self.resetShowLists();
                self.user = userId;
            }
            if (self.user != self.newUserId) {
                // Get the complete data of the user
                self.getUserView(self.user);
                // Select the first center
                if (self.user && self.centerShow.length > 0) {
                    for (let i = 0; i < self.userShow.length; i++) {
                        if (self.user == self.userShow[i].id) {
                            self.userObservations = self.userShow[i].observations;
                            if (self.userShow[i].center_ids.length > 0) {
                                self.center = self.userShow[i].center_ids[0];
                                break;
                            }
                        }
                    }
                    // Select the org
                    if (self.center && self.centerShow.length > 0) {
                        for (let i = 0; i < self.centerShow.length; i++) {
                            if (self.center == self.centerShow[i].id) {
                                self.centerObservations = self.centerShow[i].observations;
                                self.org = self.centerShow[i].org_id;
                                break;
                            }
                        }
                        // Select the server
                        if (self.org && self.serverShow.length > 0) {
                            for (let i = 0; i < self.orgShow.length; i++) {
                                if (self.org == self.orgShow[i].id) {
                                    self.orgObservations = self.orgShow[i].observations;
                                    self.server = self.orgShow[i].server_id;
                                    break;
                                }
                            }
                        }
                    }
                }
                // Not a new user
                self.newUser = false;
            } else {
                // Definetly a new user
                self.newUser = true;
            }
        },
        async saveTicket(newUser = false, setPhone = false) {
            const self = this;
            self.asyncLoading("entity,newUser,form");
            if (self.phone.nationalNumber) {
                await self.savePhone(true);
                self.ticket.phones.push(self.phone);
            }
            if (newUser && !self.ticketFailed) {
                if (self.phone.nationalNumber) self.userNew.phones.push(self.phone);
                if (self.newProfile) await self.saveProfile();
                await self.saveUser();
            } else if (setPhone) {
                if (self.phone.nationalNumber) await self.phoneToUser();
            }
            return axios.post('api/dashboard/tickets', self.ticket).then(function (response) {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.ticketFailed = false;
                self.asyncLoading("entity,newUser,form", false);
                self.$router.push('/dashboard/tickets/' + response.data.ticket.uuid + '/manage');
            }).catch(function () {
                self.ticketFailed = true;
                self.asyncLoading("entity,newUser,form", false);
            });
        },
        async saveUser() {
            const self = this;
            self.asyncLoading("entity,newUser,form");
            self.userNew.center_ids = [self.center];
            return axios.post('api/dashboard/admin/users', self.userNew).then(function (response) {
                self.user = response.data.user.id;
                self.ticket.user_id = response.data.user.id;
                self.asyncLoading("entity,newUser,form", false);
            }).catch(function () {
                self.asyncLoading("entity,newUser,form", false);
            });
        },
        saveProfile() {
            const self = this;
            self.asyncLoading("entity,newUser,form");
            return axios.post('api/dashboard/admin/profiles', self.profile).then(function (response) {
                self.userNew.profile_id = response.data.profile.id;
                self.asyncLoading("entity,newUser,form", false);
            }).catch(function () {
                self.asyncLoading("entity,newUser,form", false);
            });
        },
        async phoneToUser() {
            const self = this;
            self.asyncLoading("entity,newUser,form");
            // Find user
            for (let i = 0; i < self.userList.length; i++) {
                if (self.userList[i].id == self.ticket.user_id) {
                    self.userNew = self.userList[i];
                    break;
                }
            }
            // Save phone
            self.userNew.phone_ids.push(self.phone.id);
            // Update user
            return axios.put('api/dashboard/admin/users/' + self.userNew.id, self.userNew).then(function () {
                self.asyncLoading("entity,newUser,form", false);
            }).catch(function () {
                self.asyncLoading("entity,newUser,form", false);
            });
        },
        savePhone() {
            const self = this;
            self.asyncLoading("entity,newUser,form");
            if (self.checkPhone()) {
                let phone = {id: null, country: self.phone.country, country_code: self.phone.countryCallingCode.toString(), number: self.phone.nationalNumber}
                return axios.post('api/dashboard/admin/phones', phone).then((response) => {
                    if (response.data.status != "error") {
                        self.phone = response.data.phone;
                    }
                    self.asyncLoading("entity,newUser,form", false);
                }).catch(function () {
                    self.asyncLoading("entity,newUser,form", false);
                });
            }
        },
        checkPhone() {
            return this.phone.nationalNumber && this.phone.country && this.phone.countryCallingCode;
        },
        selectUploadFile() {
            if (!this.loading.file) {
                this.$refs.fileInput.click();
            } else {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('A file is being uploaded').toString(),
                    type: 'warning'
                });
            }
        },
        uploadFile(e) {
            const self = this;
            const formData = new FormData();
            self.asyncLoading("file");
            formData.append('file', e.target.files[0]);
            axios.post(
                'api/dashboard/tickets/attachments',
                formData,
                {
                    headers: {'Content-Type': 'multipart/form-data'},
                    onUploadProgress: function (progressEvent) {
                        self.uploadingFileProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    }.bind(this)
                }
            ).then(function (response) {
                self.asyncLoading("file", false);
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
                self.ticket.attachments.push(response.data);
            }).catch(function () {
                self.asyncLoading("file", false);
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
            });
        },
        removeAttachment(attachment) {
            this.ticket.attachments.splice(attachment, 1);
        },
        lastAPIphone() {
            const self = this;
            self.asyncLoading("entity");
            return fetch('https://www.sixtemas.com/llamada/newfile.txt', {cache: "no-store"})
                .then(async (response) => {
                    switch (response.status) {
                        case 200:
                            return response.text();
                        case 404:
                            throw response;
                    }
                })
                .then(async (template) => {
                    if (template.trim().length > 0) {
                        self.phone = parsePhoneNumber(template.trim(), "ES");
                        //self.phone = parsePhoneNumber('938454794', 'ES');
                        self.searchByPhone();
                    } else {
                        self.$notify({
                            title: 'API malfunction',
                            text: 'SixDesk recieved an empty string form the Sixtemas API',
                            type: 'error'
                        });
                    }
                    self.asyncLoading("entity", false);
                })
                .catch(function () {
                    self.asyncLoading("entity", false);
                });
        },
        searchByPhone() {
            const self = this;
            self.asyncLoading("entity");
            return axios.get('api/dashboard/tickets/call', {
                params: {
                    country: self.phone.country,
                    country_code: self.phone.countryCallingCode,
                    number: self.phone.nationalNumber
                }
            }).then(function (response) {
                let servers = response.data.items.servers;
                let orgs = response.data.items.orgs;
                let centers = response.data.items.centers;
                let users = response.data.items.users;
                self.setServerSearch(servers);
                self.setOrgSearch(orgs);
                self.setCenterSearch(centers);
                self.setUserSearch(users);
                if (users.length == 1) {
                    self.user = users[0];
                    self.selectUser();
                } else {
                    if (users.length == 0) {
                        self.user = self.newUserId;
                        self.selectUser();
                    }
                    if (centers.length == 1) {
                        self.center = centers[0];
                        self.selectCenter(users.length == 0);
                    } else if (orgs.length == 1) {
                        self.org = orgs[0];
                        self.selectOrg(users.length == 0 && centers.length == 0);
                    } else if (servers.length == 1) {
                        self.server = servers[0];
                        self.selectServer(users.length == 0 && centers.length == 0 && orgs.length == 0);
                    }
                }
                if (typeof self.server === "undefined") self.server = null;
                if (typeof self.org === "undefined") self.org = null;
                if (typeof self.center === "undefined") self.center = null;
                if (typeof self.user === "undefined") self.user = null;
                self.asyncLoading("entity", false);
            }).catch(function () {
                self.asyncLoading("entity", false);
            });
        },
        searchByCode() {
            const self = this;
            let orgCodeTemp = null;
            let userCodeTemp = null;
            if (!self.orgCode || !self.userCode) return;
            // Search the orgs
            for (let i = 0; i < self.orgList.length; i++) {
                orgCodeTemp = self.orgList[i].support_code;
                if (self.orgCode == orgCodeTemp) {
                    self.org = self.orgList[i].id;
                    self.selectOrg();
                    break;
                }
            }
            // Search in org's users
            for (let i = 0; i < self.userShow.length; i++) {
                userCodeTemp = self.userShow[i].support_code ?? '';
                if (self.userCode.toLowerCase() == userCodeTemp.toLowerCase()) {
                    self.user = self.userShow[i].id;
                    self.selectUser();
                    break;
                }
            }
        },
        inputSupport(event) {
            // Init
            let target = event.srcElement || event.target;
            let maxLength = parseInt(target.attributes["maxlength"].value, 10);
            let currentLength = target.value.length;
            // Next position
            if (currentLength >= maxLength) {
                let next = target;
                while (next = next.nextElementSibling) {
                    if (next == null) break;
                    if (next.tagName.toLowerCase() === "input") {
                        next.focus();
                        break;
                    }
                }
            }
            // Previous position
            else if (currentLength === 0) {
                let previous = target;
                while (previous = previous.previousElementSibling) {
                    if (previous == null) break;
                    if (previous.tagName.toLowerCase() === "input") {
                        previous.focus();
                        break;
                    }
                }
            }
        },
        resetShowLists() { 
            this.setServerList(this.serverList);
            this.setOrgList(this.orgList);
            this.setCenterList(this.centerList);
            this.setUserList(this.userList);
        },
        resetSearchLists() {
            this.setServerSearch();
            this.setOrgSearch();
            this.setCenterSearch();
            this.setUserSearch();
        },
        clearEntities() {
            this.asyncLoading("entity");
            this.resetShowLists();
            this.resetSearchLists();
            this.user = null;
            this.newUser = false;
            this.phone.nationalNumber = null;
            this.applyPhone = false;
            this.clearNewUser();
            this.orgObservations = null;
            this.centerObservations = null;
            this.userObservations = null;
            this.$refs.entity.scrollIntoView({behavior: 'smooth'});
            this.asyncLoading("entity", false);
        },
        clearNewUser() {
            this.asyncLoading("newUser");
            this.userNew.name = null;
            this.userNew.email = null;
            this.userNew.profile_id = null;
            this.profile.name = null;
            this.newProfile = false;
            this.$refs.newUser.scrollIntoView({behavior: 'smooth'});
            this.asyncLoading("newUser", false);
        },
        clearDetails() {
            this.asyncLoading("form");
            this.ticket.subject = null;
            this.ticket.department_id = null;
            this.ticket.status_id = 1;
            this.ticket.priority_id = 1;
            this.ticket.body = '';
            this.ticket.sendMail = false;
            this.$refs.form.scrollIntoView({behavior: 'smooth'});
            this.asyncLoading("form", false);
        },
        asyncLoading(types = "", mode = true) {
            const self = this;
            types = types.split(',');
            for (let i = 0; i < types.length; i++) {
                if (mode) {
                    switch (types[i]) {
                        case "entity":
                            self.asyncLoad.entity++;
                            if (self.asyncLoad.entity == 1) {
                                self.loading.entity = true;
                            }
                            break;
                        case "newUser":
                            self.asyncLoad.newUser++;
                            if (self.asyncLoad.newUser == 1) {
                                self.loading.newUser = true;
                            }
                            break;
                        case "form":
                            self.asyncLoad.form++;
                            if (self.asyncLoad.form == 1) {
                                self.loading.form = true;
                            }
                            break;
                        case "file":
                            self.asyncLoad.file++;
                            if (self.asyncLoad.file == 1) {
                                self.loading.file = true;
                            }
                            break;
                        case "details":
                            self.asyncLoad.details++;
                            if (self.asyncLoad.details == 1) {
                                self.loading.details = true;
                            }
                            break;
                        case "password":
                            self.asyncLoad.password++;
                            if (self.asyncLoad.password == 1) {
                                self.loading.password = true;
                            }
                            break;
                        case "reply":
                            self.asyncLoad.reply++;
                            if (self.asyncLoad.reply == 1) {
                                self.loading.reply = true;
                            }
                            break;
                        default:
                            self.asyncLoad.loading++;
                            if (self.asyncLoad.loading == 1) {
                                self.loading = true;
                            }
                            break;
                    }
                } else {
                    switch (types[i]) {
                        case "entity":
                            self.asyncLoad.entity--;
                            if (self.asyncLoad.entity <= 0) {
                                self.asyncLoad.entity = 0;
                                self.loading.entity = false;
                            }
                            break;
                        case "newUser":
                            self.asyncLoad.newUser--;
                            if (self.asyncLoad.newUser <= 0) {
                                self.asyncLoad.newUser = 0;
                                self.loading.newUser = false;
                            }
                            break;
                        case "form":
                            self.asyncLoad.form--;
                            if (self.asyncLoad.form <= 0) {
                                self.asyncLoad.form = 0;
                                self.loading.form = false;
                            }
                            break;
                        case "file":
                            self.asyncLoad.file--;
                            if (self.asyncLoad.file <= 0) {
                                self.asyncLoad.file = 0;
                                self.loading.file = false;
                            }
                            break;
                        case "details":
                            self.asyncLoad.details--;
                            if (self.asyncLoad.details <= 0) {
                                self.asyncLoad.details = 0;
                                self.loading.details = false;
                            }
                            break;
                        case "password":
                            self.asyncLoad.password--;
                            if (self.asyncLoad.password <= 0) {
                                self.asyncLoad.password = 0;
                                self.loading.password = false;
                            }
                            break;
                        case "reply":
                            self.asyncLoad.reply--;
                            if (self.asyncLoad.reply <= 0) {
                                self.asyncLoad.reply = 0;
                                self.loading.reply = false;
                            }
                            break;
                        default:
                            self.asyncLoad.loading--;
                            if (self.asyncLoad.loading <= 0) {
                                self.asyncLoad.loading = 0;
                                self.loading = false;
                            }
                            break;
                    }
                }
            }
        }
    },
}
</script>
<style>
.fade-enter-active, .fade-leave-active {
  transition: opacity .6s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
